import React, { useContext } from "react";
import { sortBy } from "lodash";
import { useHistory, useLocation, useParams } from "react-router";
import { OBJECT_TYPE, WORKFLOW_STEP_STATUS } from "common/constants";
import { buildLink, INFO_PANEL_TAB_NAME } from "common/routing";
import { getObjectDisplay } from "common-client/utils/customObject";
import { GetWorkflowQuery, useGetWorkflowQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { Button } from "../Common/Button";
import { ButtonLink } from "../Common/ButtonLink";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import { ProgressBar } from "../Common/ProgressBar";
import { StyleVariant, Tag } from "../Common/Tag";
import { Body, Title } from "../Common/Typography";
import { ApplicationInfo, WorkflowContainer } from "./__styles__/Workflow";
import { WorkflowStep } from "./WorkflowStep";

export const Workflow = () => {
  const { state } = useLocation<{ prevLocation?: string }>();
  const prevLocation = state?.prevLocation ?? buildLink("map");
  const { workflowId } = useParams<{
    workflowId: string;
  }>();

  const { data, loading } = useGetWorkflowQuery({
    variables: { workflowId },
    fetchPolicy: "network-only",
  });

  if (loading || !data?.workflow) {
    return null;
  }

  const workflowName = data.workflow.workflowDefinition.name;

  return (
    <FullPageFormLayout
      subtitle={workflowName}
      prevLocation={prevLocation}
      width="wide"
    >
      <WorkflowContent workflow={data.workflow} />
    </FullPageFormLayout>
  );
};

export const WorkflowContent = ({
  workflow,
}: {
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
}) => {
  const history = useHistory();
  const { isPublic } = useContext(AuthContext);

  const objectDisplay =
    workflow.property ?
      getObjectDisplay({
        objectType: OBJECT_TYPE.PROPERTY,
        object: workflow.property,
      })
    : null;

  const orderedWorkflowSteps = sortBy(
    workflow.workflowSteps,
    "workflowStepDefinition.order",
  );

  const completedCount = workflow.workflowSteps.filter(
    step =>
      step.status &&
      [
        WORKFLOW_STEP_STATUS.FAILED,
        WORKFLOW_STEP_STATUS.SUCCEEDED,
        WORKFLOW_STEP_STATUS.SKIPPED,
      ].includes(step.status),
  ).length;

  const totalSteps = workflow.workflowSteps.length;

  const propertyUrl =
    isPublic ?
      buildLink("residentProperty", {
        propertyId: workflow.property!.id,
      })
    : buildLink(
        "map",
        {},
        {
          propertyId: workflow.property!.id,
          lat: workflow.property!.latitude.toString(),
          lng: workflow.property!.longitude.toString(),
          address: workflow.property!.fullAddress,
          tab: INFO_PANEL_TAB_NAME.WORKFLOWS,
        },
      );

  return (
    <WorkflowContainer isPublic={isPublic}>
      {isPublic && (
        <ButtonLink
          styleVariant="primary"
          size="medium"
          leftIconName="chevron-left"
          onClick={() =>
            history.push(
              buildLink("listWorkflows", {
                workflowDefinitionId: workflow.workflowDefinition.id,
              }),
            )
          }
          style={{ width: "fit-content" }}
        >
          {workflow.workflowDefinition.name}
        </ButtonLink>
      )}
      <ApplicationInfo>
        <FlexColumn style={{ gap: spacing.s.value }}>
          <FlexRow
            style={{
              gap: spacing.s.value,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Body size="small" type="emphasis" color="contentSecondary">
              APPLICATION
            </Body>
            <Tag styleVariant={workflow.status.display as StyleVariant}>
              {workflow.status.text}
            </Tag>
          </FlexRow>
        </FlexColumn>

        <FlexColumn style={{ gap: spacing.s.value }}>
          {objectDisplay && (
            <Title size="default" type="semiBold">
              {objectDisplay}
            </Title>
          )}
        </FlexColumn>
        {workflow.property && (
          <Button
            styleVariant="secondary"
            size="small"
            fitContent
            onClick={() => history.push(propertyUrl)}
          >
            View property
          </Button>
        )}
      </ApplicationInfo>
      <ProgressBar value={completedCount} max={totalSteps} />
      <FlexColumn style={{ gap: spacing.m.value }}>
        {orderedWorkflowSteps.map(step => (
          <WorkflowStep
            key={step.id}
            workflowStep={step}
            objectId={workflow.property?.id}
            objectType={
              workflow.property?.id ? OBJECT_TYPE.PROPERTY : undefined
            }
          />
        ))}
      </FlexColumn>
    </WorkflowContainer>
  );
};
